import { useAuth } from "@clerk/react";
import { findErrorTraceId, safeFailureLogFields } from "@cleo/client-runtime/errors";
import { redactCredentialText } from "@cleo/shared/credentialRedaction";
import {
  isAtomCommandInterrupted,
  settlePromise,
  squashAtomCommandFailure,
} from "@cleo/client-runtime/state/runtime";
import { useState } from "react";

import { toastManager } from "../components/ui/toast";
import { relayEnvironmentDiscovery } from "../state/relay";
import { useAtomCommand } from "../state/use-atom-command";
import {
  linkPrimaryEnvironment as linkPrimaryEnvironmentAtom,
  unlinkPrimaryEnvironment as unlinkPrimaryEnvironmentAtom,
  updatePrimaryEnvironmentPreferences as updatePrimaryEnvironmentPreferencesAtom,
} from "./linkEnvironmentAtoms";
import { usePrimaryCloudLinkState } from "./primaryCloudLinkState";
import { resolveRelayClerkTokenOptions } from "./publicConfig";

export interface CloudLinkDesiredState {
  readonly managedTunnel: boolean;
  readonly publish: boolean;
  /** False when only the tunnel changes: the publish preference is then left as the
      environment holds it (W-42d3a4ff: writing the switch's current value stored a "false"
      no one chose, and a new link's notifications never left the Desk). */
  readonly writePublish?: boolean;
}

/**
 * Logs a failed Cleo Connect update with a fixed description, the error tags,
 * status and trace ID: no error object and no message. A request error can hold
 * the request and its credentials, and any message can quote them; the
 * renderer console keeps whatever is logged (W-b8299199). The message is
 * returned, redacted, for the person's own screen.
 */
export function logCloudConnectUpdateFailure(cause: unknown): {
  readonly message: string;
  readonly traceId: string | null;
} {
  const message = redactCredentialText(
    cause instanceof Error ? cause.message : "Could not update Cleo Connect access.",
  );
  console.error(
    "[cleo-connect] Could not update Cleo Connect",
    safeFailureLogFields("Cleo Connect update failed.", cause),
  );
  return { message, traceId: findErrorTraceId(cause) };
}

/**
 * Drives the primary environment's Cleo Connect link. Cleo Connect (managed
 * tunnel) and agent-activity publishing are independent capabilities backed by
 * a single relay link, so consumers express the full desired state and
 * `reconcileCloudState` applies it: unlink when neither is wanted, otherwise
 * (re)link with the mode the managed-tunnel bit implies and set the publish
 * preference. Re-linking only happens when the managed-tunnel mode actually
 * changes, so flipping publish alone is cheap.
 */
export function useCloudLinkController() {
  const { getToken, isSignedIn } = useAuth();
  const refreshRelayEnvironments = useAtomCommand(relayEnvironmentDiscovery.refresh, {
    reportFailure: false,
  });
  const linkPrimaryEnvironment = useAtomCommand(linkPrimaryEnvironmentAtom, {
    reportFailure: false,
  });
  const unlinkPrimaryEnvironment = useAtomCommand(unlinkPrimaryEnvironmentAtom, {
    reportFailure: false,
  });
  const updatePrimaryEnvironmentPreferences = useAtomCommand(
    updatePrimaryEnvironmentPreferencesAtom,
    { reportFailure: false },
  );
  const primaryCloudLinkState = usePrimaryCloudLinkState();
  const [operationError, setOperationError] = useState<string | null>(null);

  const reportUpdateFailure = (cause: unknown) => {
    const { message, traceId } = logCloudConnectUpdateFailure(cause);
    setOperationError(traceId ? `${message} Trace ID: ${traceId}` : message);
    toastManager.add({
      type: "error",
      title: "Could not update Cleo Connect",
      description: message,
      data: traceId
        ? {
            secondaryActionProps: {
              children: "Copy trace ID",
              onClick: () => void navigator.clipboard?.writeText(traceId),
            },
          }
        : undefined,
    });
  };

  // Older environment servers predate the managedTunnelActive field; for them a
  // link always implies a managed tunnel, so fall back to `linked`.
  const managedTunnelActive =
    primaryCloudLinkState.data?.managedTunnelActive ?? primaryCloudLinkState.data?.linked ?? false;
  const publishAgentActivity = primaryCloudLinkState.data?.publishAgentActivity ?? false;
  const linked = primaryCloudLinkState.data?.linked ?? false;

  const reconcileCloudState = async (desired: CloudLinkDesiredState): Promise<boolean> => {
    setOperationError(null);
    const target = primaryCloudLinkState.target;
    if (!target) {
      reportUpdateFailure(new Error("Local environment is not ready yet."));
      return false;
    }
    const tokenResult = await settlePromise(() => getToken(resolveRelayClerkTokenOptions()));
    const wantsLink = desired.managedTunnel || desired.publish;

    // A failure after this point may follow a partially applied mutation (e.g.
    // the link succeeded but the preference update did not), so every exit —
    // success or failure — refreshes the rendered state to whatever the server
    // actually holds now.
    if (!wantsLink) {
      // Turning publishing off (the switch, not a tunnel toggle) is stored before the unlink,
      // and unlink keeps an explicit "false", so linking again later stays off (W-42d3a4ff).
      if (desired.writePublish !== false && !desired.publish) {
        const offResult = await updatePrimaryEnvironmentPreferences({
          target,
          publishAgentActivity: false,
        });
        // An "off" that was not stored is not an "off": stop before unlinking and say so,
        // or the next link would turn publishing back on.
        if (offResult._tag === "Failure") {
          if (!isAtomCommandInterrupted(offResult)) {
            reportUpdateFailure(squashAtomCommandFailure(offResult));
          }
          primaryCloudLinkState.refresh();
          return false;
        }
      }
      // Unlink works without a relay token — a failed token read must not
      // leave the user unable to turn Cleo Connect off.
      const unlinkResult = await unlinkPrimaryEnvironment({
        target,
        clerkToken: tokenResult._tag === "Success" ? (tokenResult.value ?? null) : null,
      });
      if (unlinkResult._tag === "Failure") {
        if (!isAtomCommandInterrupted(unlinkResult)) {
          reportUpdateFailure(squashAtomCommandFailure(unlinkResult));
        }
        primaryCloudLinkState.refresh();
        return false;
      }
    } else {
      if (tokenResult._tag === "Failure") {
        reportUpdateFailure(squashAtomCommandFailure(tokenResult));
        return false;
      }
      const clerkToken = tokenResult.value;
      if (!clerkToken) {
        reportUpdateFailure(new Error("Sign in to Cleo Connect before enabling this."));
        return false;
      }
      // The person's publish choice is written before the link applies, so a link's default
      // (publishing on when no one has chosen, W-42d3a4ff) never runs ahead of an explicit "off".
      if (desired.writePublish !== false) {
        const prefResult = await updatePrimaryEnvironmentPreferences({
          target,
          publishAgentActivity: desired.publish,
        });
        if (prefResult._tag === "Failure") {
          if (!isAtomCommandInterrupted(prefResult)) {
            reportUpdateFailure(squashAtomCommandFailure(prefResult));
          }
          primaryCloudLinkState.refresh();
          return false;
        }
      }
      if (!linked || managedTunnelActive !== desired.managedTunnel) {
        const linkResult = await linkPrimaryEnvironment({
          target,
          clerkToken,
          mode: desired.managedTunnel ? "managed" : "publish_only",
        });
        if (linkResult._tag === "Failure") {
          if (!isAtomCommandInterrupted(linkResult)) {
            reportUpdateFailure(squashAtomCommandFailure(linkResult));
          }
          primaryCloudLinkState.refresh();
          return false;
        }
      }
    }

    primaryCloudLinkState.refresh();
    const refreshResult = await refreshRelayEnvironments();
    if (refreshResult._tag === "Failure" && !isAtomCommandInterrupted(refreshResult)) {
      reportUpdateFailure(squashAtomCommandFailure(refreshResult));
      return false;
    }
    return true;
  };

  return {
    isSignedIn,
    linkState: primaryCloudLinkState,
    linked,
    managedTunnelActive,
    publishAgentActivity,
    operationError,
    reconcileCloudState,
  };
}
